import { useTheme } from '@emotion/react';
import PropTypes from 'prop-types';

import Box from 'shopper/components/Box';
import Button from 'shopper/components/Button';
import Flex from 'shopper/components/Flex';
import Icon from 'shopper/components/Icon';

import { sendEvent } from 'lib/gtag';
import { formatLinkWithUtms } from 'lib/share';

import Anchor from '../Anchor';
import Image from '../Image';
import TextWithEllipsis from '../TextWithEllipsis';
import PriceDesktop from './PriceDesktop';
import PriceMobile from './PriceMobile';

import ACTIONS from 'constants/action';

const ProductItem = ({
  gaEventCategory,
  product: { productImage, productTitle },
  prices: [{ priceValue, redirectUrl, storeTitle }],
  utmMedium,
}) => {
  const theme = useTheme();

  const onGoToStoreClick = () =>
    sendEvent({ action: 'go_offer_comparai', category: gaEventCategory });

  return (
    <Anchor
      target="_blank"
      rel="noopener"
      href={formatLinkWithUtms({
        redirectUrl,
        utmCampaign: productTitle,
        utmMedium,
      })}
      onClick={onGoToStoreClick}
    >
      <Box>
        <Flex
          justifyContent="space-between"
          borderBottom="1px solid"
          borderRadius={4}
          p={4}
          borderColor={theme.app.border}
          alignItems={['flex-start', null, 'center']}
          flexDirection={['column', null, 'row']}
        >
          <Flex alignItems={['flex-start', null, 'center']}>
            {productImage && (
              <Image
                sizes="(max-width: 768px) 140px, 80px"
                borderRadius={4}
                src={productImage}
                alt={productTitle}
                width={[61, null, 80]}
                height={[61, null, 80]}
                mr={[2, null, 4]}
              />
            )}
            <Flex flexDirection="column">
              <TextWithEllipsis maxLines={3} mr={[null, null, 6]} bold>
                {productTitle}
              </TextWithEllipsis>
              <PriceMobile priceValue={priceValue} storeTitle={storeTitle} />
            </Flex>
          </Flex>
          <Flex
            alignItems="center"
            flexDirection={['column', null, 'row']}
            width={['100%', null, 'unset']}
          >
            <PriceDesktop priceValue={priceValue} storeTitle={storeTitle} />
            <Box
              mt={[4, null, 0]}
              width={['100%', null, 'unset']}
              display={['none', null, 'block']}
            >
              <Button size="size2" iconRight={<Icon name="link-blank" />} full>
                {ACTIONS.GO_TO_STORE}
              </Button>
            </Box>
          </Flex>
        </Flex>
      </Box>
    </Anchor>
  );
};

ProductItem.propTypes = {
  gaEventCategory: PropTypes.string.isRequired,
  product: PropTypes.shape({
    productTitle: PropTypes.string.isRequired,
    productImage: PropTypes.string.isRequired,
    productUrl: PropTypes.string.isRequired,
  }).isRequired,
  prices: PropTypes.arrayOf(
    PropTypes.shape({
      priceParcels: PropTypes.string,
      priceValue: PropTypes.string.isRequired,
      productTitle: PropTypes.string.isRequired,
      productImage: PropTypes.string.isRequired,
      redirectUrl: PropTypes.string.isRequired,
      storeImage: PropTypes.string.isRequired,
      storeTitle: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default ProductItem;
